import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import {
  adminSendNotification,
  type SendNotificationInput,
} from "@/lib/callables";
import type { AdminNotification } from "@/types";
import { fetchCollection, orderBy } from "./use-firestore";
import { usePagedQuery } from "./use-paged-query";

/**
 * O histórico dos envios dirigidos, uma página por vez.
 *
 * `sentAt` é escrito pela callable em todo documento, então `orderBy` é seguro.
 * Antes a tela mostrava só os cinquenta últimos; agora dá para voltar até o
 * primeiro envio, página a página.
 */
export function useNotificationHistory() {
  return usePagedQuery<AdminNotification>({
    key: queryKeys.notifications(),
    path: "adminNotifications",
    constraints: [orderBy("sentAt", "desc")],
    pageSize: 10,
  });
}

export function useSendNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendNotificationInput) => adminSendNotification(input),
    // O histórico é gravado pela própria callable, inclusive quando o envio
    // falha: por isso a invalidação vale nos dois casos.
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications() }),
    onError: () => qc.invalidateQueries({ queryKey: queryKeys.notifications() }),
  });
}

/** Os vendedores com conta, que é quem pode receber push de vendedor. */
export function useSellerUsers() {
  return useQuery({
    queryKey: ["sellerUsers"],
    queryFn: () =>
      fetchCollection<{ id: string; name?: string; email?: string; sellerId?: string }>(
        "sellerUsers",
      ),
    select: (rows) =>
      [...rows].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR")),
  });
}
